import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  CityId,
  City,
  Station,
  Line,
  Connection,
  FareData,
  StationGraph,
  MetroData,
} from '../types/metro.types';
import { CITIES, getCityById } from '../config/cities.config';
import { buildGraph } from '../services/routeFinder';

interface MetroContextType {
  // City
  selectedCity: CityId | null;
  setSelectedCity: (city: CityId | null) => void;
  currentCity: City | null;
  cities: City[];
  operationalCities: City[];

  // Metro Data
  stations: Station[];
  lines: Line[];
  connections: Connection[];
  fares: FareData | null;
  graph: StationGraph | null;

  // Loading
  isLoading: boolean;
  error: string | null;

  // Helpers
  getStationById: (id: string) => Station | undefined;
  getLineById: (id: string) => Line | undefined;
  getStationsByLine: (lineId: string) => Station[];
  searchStations: (query: string) => Station[];
}

const MetroContext = createContext<MetroContextType | null>(null);

export const useMetro = () => {
  const context = useContext(MetroContext);
  if (!context) {
    throw new Error('useMetro must be used within MetroProvider');
  }
  return context;
};

interface MetroProviderProps {
  children: ReactNode;
}

export const MetroProvider = ({ children }: MetroProviderProps) => {
  const [selectedCity, setSelectedCityState] = useState<CityId | null>(null);
  const [metroData, setMetroData] = useState<MetroData | null>(null);
  const [graph, setGraph] = useState<StationGraph | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load saved city from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('selectedCity');
    if (saved && CITIES.some(c => c.id === saved)) {
      setSelectedCityState(saved as CityId);
    }
  }, []);

  // Load metro data when city changes
  useEffect(() => {
    if (!selectedCity) {
      setMetroData(null);
      setGraph(null);
      return;
    }

    const loadCityData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Dynamically import city data
        const [stationsModule, linesModule, connectionsModule, faresModule] = await Promise.all([
          import(`../data/${selectedCity}/stations.json`),
          import(`../data/${selectedCity}/lines.json`),
          import(`../data/${selectedCity}/connections.json`),
          import(`../data/${selectedCity}/fares.json`),
        ]);

        // Extract actual data from wrapper objects (data files have { metadata, stations/lines/connections } structure)
        const stationsData = stationsModule.default || stationsModule;
        const linesData = linesModule.default || linesModule;
        const connectionsData = connectionsModule.default || connectionsModule;
        const faresData = faresModule.default || faresModule;

        const data: MetroData = {
          stations: stationsData.stations || stationsData,
          lines: linesData.lines || linesData,
          connections: connectionsData.connections || connectionsData,
          fares: faresData.fares || faresData,
        };

        setMetroData(data);
        setGraph(buildGraph(data.connections));
      } catch (err) {
        console.error('Failed to load city data:', err);
        setError(`Failed to load data for ${selectedCity}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadCityData();
  }, [selectedCity]);

  const setSelectedCity = (city: CityId | null) => {
    setSelectedCityState(city);
    if (city) {
      localStorage.setItem('selectedCity', city);
    } else {
      localStorage.removeItem('selectedCity');
    }
  };

  const currentCity = selectedCity ? getCityById(selectedCity) || null : null;
  const operationalCities = CITIES.filter(c => c.isOperational);

  // Helper functions
  const getStationById = (id: string) => metroData?.stations.find(s => s.id === id);
  const getLineById = (id: string) => metroData?.lines.find(l => l.id === id);

  const getStationsByLine = (lineId: string) => {
    const line = getLineById(lineId);
    if (!line) return [];
    return line.stations
      .map(id => getStationById(id))
      .filter((s): s is Station => s !== undefined);
  };

  const searchStations = (query: string) => {
    if (!metroData || !query.trim()) return [];
    const q = query.toLowerCase();
    return metroData.stations.filter(
      s => s.name.toLowerCase().includes(q) || s.id.toLowerCase().includes(q)
    );
  };

  const value: MetroContextType = {
    selectedCity,
    setSelectedCity,
    currentCity,
    cities: CITIES,
    operationalCities,
    stations: metroData?.stations || [],
    lines: metroData?.lines || [],
    connections: metroData?.connections || [],
    fares: metroData?.fares || null,
    graph,
    isLoading,
    error,
    getStationById,
    getLineById,
    getStationsByLine,
    searchStations,
  };

  return (
    <MetroContext.Provider value={value}>
      {children}
    </MetroContext.Provider>
  );
};
